import { supabase } from '../lib/supabase';

/**
 * Leaderboard Service
 * Handles all leaderboard data fetching for XP rankings, streaks, and content mastery
 */

/**
 * Fetches top users by lifetime XP with weekly gains
 * @param {Object} options - Filter options
 * @param {string} options.userType - 'all', 'user', 'editor', 'talent'
 * @param {number} options.limit - Number of results to return
 * @param {string} options.timePeriod - 'daily', 'weekly', 'monthly', 'all-time'
 */
export const getXPLeaderboard = async ({ userType = 'all', limit = 50, timePeriod = 'all-time' }) => {
  try {
    let query = supabase?.from('user_profiles')?.select('id, username, full_name, profile_photo_url, total_lifetime_xp, current_level, role, created_at')?.eq('is_active', true)?.order('total_lifetime_xp', { ascending: false })?.limit(limit);

    // Filter by user type
    if (userType !== 'all') {
      query = query?.eq('role', userType);
    }

    const { data: profiles, error } = await query;

    if (error) throw error;

    // Calculate weekly XP gains and position changes for each user
    const usersWithStats = await Promise.all(
      profiles?.map(async (profile) => {
        // Get weekly XP gains
        const weekAgo = new Date();
        weekAgo?.setDate(weekAgo?.getDate() - 7);

        const { data: weeklyXP, error: xpError } = await supabase?.from('user_xp_events')?.select('amount')?.eq('user_id', profile?.id)?.gte('created_at', weekAgo?.toISOString());

        if (xpError) console.error('Error fetching weekly XP:', xpError);

        const weeklyGain = weeklyXP?.reduce((sum, event) => sum + (event?.amount || 0), 0) || 0;

        return {
          ...profile,
          weeklyXpGain: weeklyGain,
          position: profiles?.indexOf(profile) + 1,
          positionChange: 0 // This would require historical tracking
        };
      })
    );

    return usersWithStats;
  } catch (error) {
    console.error('Error fetching XP leaderboard:', error);
    throw error;
  }
};

/**
 * Fetches top users by achievement streaks
 * @param {Object} options - Filter options
 */
export const getStreakLeaderboard = async ({ userType = 'all', limit = 50 }) => {
  try {
    let query = supabase?.from('user_profiles')?.select('id, username, full_name, profile_photo_url, daily_streak, longest_streak, role')?.eq('is_active', true)?.order('daily_streak', { ascending: false })?.limit(limit);

    if (userType !== 'all') {
      query = query?.eq('role', userType);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map((user, index) => ({
      ...user,
      position: index + 1,
      streakMilestone: getStreakMilestone(user?.daily_streak)
    })) || [];
  } catch (error) {
    console.error('Error fetching streak leaderboard:', error);
    throw error;
  }
};

/**
 * Fetches content mastery leaderboard based on completion rates
 * @param {Object} options - Filter options
 */
export const getContentMasteryLeaderboard = async ({ userType = 'all', limit = 50, category = 'all' }) => {
  try {
    // First get all users
    let userQuery = supabase?.from('user_profiles')?.select('id, username, full_name, profile_photo_url, role')?.eq('is_active', true);

    if (userType !== 'all') {
      userQuery = userQuery?.eq('role', userType);
    }

    const { data: users, error: userError } = await userQuery;
    if (userError) throw userError;

    // Calculate completion rates for each user
    const usersWithStats = await Promise.all(
      users?.map(async (user) => {
        const { data: progressData, error: progressError } = await supabase?.from('user_lesson_progress')?.select('lesson_status, quiz_score, is_quiz_perfect')?.eq('user_id', user?.id);

        if (progressError) {
          console.error('Error fetching progress:', progressError);
          return null;
        }

        const total = progressData?.length || 0;
        const completed = progressData?.filter(p => p?.lesson_status === 'completed')?.length || 0;
        const perfectQuizzes = progressData?.filter(p => p?.is_quiz_perfect)?.length || 0;
        const averageQuizScore = progressData?.length > 0
          ? progressData?.reduce((sum, p) => sum + (p?.quiz_score || 0), 0) / total
          : 0;

        const completionRate = total > 0 ? (completed / total) * 100 : 0;

        return {
          ...user,
          completionRate,
          lessonsCompleted: completed,
          totalLessons: total,
          perfectQuizzes,
          averageQuizScore: Math.round(averageQuizScore)
        };
      })
    );

    // Filter out null values and sort by completion rate
    const validUsers = usersWithStats?.filter(user => user !== null)?.sort((a, b) => b?.completionRate - a?.completionRate)?.slice(0, limit)?.map((user, index) => ({
        ...user,
        position: index + 1
      }));

    return validUsers;
  } catch (error) {
    console.error('Error fetching content mastery leaderboard:', error);
    throw error;
  }
};

/**
 * Fetches rising stars - users with recent top performance
 * @param {Object} options - Filter options
 */
export const getRisingStarsLeaderboard = async ({ userType = 'all', limit = 50, days = 7 }) => {
  try {
    const daysAgo = new Date();
    daysAgo?.setDate(daysAgo?.getDate() - days);

    // Get all active users
    let userQuery = supabase?.from('user_profiles')?.select('id, username, full_name, profile_photo_url, role, created_at')?.eq('is_active', true);

    if (userType !== 'all') {
      userQuery = userQuery?.eq('role', userType);
    }

    const { data: users, error: userError } = await userQuery;
    if (userError) throw userError;

    // Calculate recent momentum for each user
    const usersWithMomentum = await Promise.all(
      users?.map(async (user) => {
        // Get recent XP gains
        const { data: recentXP, error: xpError } = await supabase?.from('user_xp_events')?.select('amount, created_at')?.eq('user_id', user?.id)?.gte('created_at', daysAgo?.toISOString());

        if (xpError) {
          console.error('Error fetching recent XP:', xpError);
          return null;
        }

        const totalRecentXP = recentXP?.reduce((sum, event) => sum + (event?.amount || 0), 0) || 0;
        
        // Get recent completions
        const { data: recentCompletions, error: progressError } = await supabase?.from('user_lesson_progress')?.select('completed_at')?.eq('user_id', user?.id)?.eq('lesson_status', 'completed')?.gte('completed_at', daysAgo?.toISOString());

        if (progressError) {
          console.error('Error fetching recent completions:', progressError);
        }

        const recentAchievements = recentCompletions?.length || 0;
        const momentum = totalRecentXP + (recentAchievements * 50); // Combined score

        return {
          ...user,
          recentXP: totalRecentXP,
          recentAchievements,
          momentum,
          daysTracked: days
        };
      })
    );

    // Filter, sort, and rank
    const validUsers = usersWithMomentum?.filter(user => user !== null && user?.momentum > 0)?.sort((a, b) => b?.momentum - a?.momentum)?.slice(0, limit)?.map((user, index) => ({
        ...user,
        position: index + 1
      }));

    return validUsers;
  } catch (error) {
    console.error('Error fetching rising stars leaderboard:', error);
    throw error;
  }
};

/**
 * Gets detailed user stats for leaderboard entry hover states
 */
export const getUserDetailedStats = async (userId) => {
  try {
    const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

    if (profileError) throw profileError;

    // Get lesson progress stats
    const { data: progressData, error: progressError } = await supabase?.from('user_lesson_progress')?.select('*')?.eq('user_id', userId);

    if (progressError) throw progressError;

    // Get earned badges
    const { data: badgeData, error: badgeError } = await supabase?.from('user_badges')?.select('*, badges(*)')?.eq('user_id', userId)?.eq('status', 'earned');

    if (badgeError) throw badgeError;

    return {
      profile,
      lessonsCompleted: progressData?.filter(p => p?.lesson_status === 'completed')?.length || 0,
      totalLessons: progressData?.length || 0,
      earnedBadges: badgeData?.length || 0
    };
  } catch (error) {
    console.error('Error fetching user detailed stats:', error);
    throw error;
  }
};

/**
 * Helper function to determine streak milestone
 */
const getStreakMilestone = (streak) => {
  if (streak >= 365) return '🔥 Year Long Streak!';
  if (streak >= 180) return '🎯 Half Year Streak!';
  if (streak >= 100) return '💪 100 Day Streak!';
  if (streak >= 50) return '⭐ 50 Day Streak!';
  if (streak >= 30) return '🌟 Month Long Streak!';
  if (streak >= 7) return '✨ Week Long Streak!';
  return null;
};

const leaderboardService = {
  getXPLeaderboard,
  getStreakLeaderboard,
  getContentMasteryLeaderboard,
  getRisingStarsLeaderboard,
  getUserDetailedStats
};

export default leaderboardService;